import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getInventoryCapitalPlanTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import { SelectDropdown } from '../SelectDropdown';
import { InventoryCapitalPlanSummaryBar } from './inventoryCapitalPlanComponents/InventoryCapitalPlanSummaryBar';
import {
  InventoryCapitalPlanCategoryRail,
  InventoryCapitalPlanMonthRail,
} from './inventoryCapitalPlanComponents/InventoryCapitalPlanSideRails';
import { InventoryCapitalPlanMainTable } from './inventoryCapitalPlanComponents/InventoryCapitalPlanMainTable';
import {
  buildInventoryCapitalCategorySummaries,
  buildInventoryCapitalMonthSummaries,
  filterInventoryCapitalPlanRows,
  scopeInventoryCapitalPlanRows,
  summarizeInventoryCapitalPlanRows,
} from '../../lib/purchasePlanningHelpers';

const EXCEPTION_FILTER_OPTIONS = [
  { value: 'all', label: 'All finance rows' },
  { value: 'exceptions', label: 'Rows with issues' },
  { value: 'missing-cost', label: 'Missing unit cost' },
  { value: 'unscheduled', label: 'Missing PO date' },
];

const selectDropdownClassnames = {
  selectClassname:
    '!h-9 !min-w-[180px] !max-w-[210px] !w-full !bg-transparent !text-sm !outline-none !bg-white',
  dropdownClassname: '!p-1',
  mainWrapperClassname: '!w-auto',
  itemValueClassname: '!text-sm !px-1.5 hover:!bg-primary hover:!text-white',
  selectedItemValueClassname: '!bg-primary !text-white',
};

const InventoryCapitalPlanTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
}) => {
  const dispatch = useDispatch();
  const { inventoryCapitalPlanTableData, loadingKeys } = useSelector(
    state => state.demandPlanning
  );

  const [localSearch, setLocalSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [exceptionFilter, setExceptionFilter] = useState('all');

  const inventoryCapitalPlanTableLoading = loadingKeys.includes(
    DemandPlanningLoadingKeys.getInventoryCapitalPlanTableData
  );

  const rows = useMemo(() => {
    const raw = inventoryCapitalPlanTableData?.rows;
    return scopeInventoryCapitalPlanRows(arrayHasItems(raw) ? raw : []);
  }, [inventoryCapitalPlanTableData]);

  const categoryOptions = useMemo(() => {
    const categories = Array.from(new Set(rows.map(row => row.category).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right))
      .map(category => ({ value: category, label: category }));
    return [{ value: 'all', label: 'All categories' }, ...categories];
  }, [rows]);

  const monthOptions = useMemo(() => {
    const months = Array.from(new Set(rows.map(row => row.month).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right))
      .map(month => ({ value: month, label: month }));
    return [{ value: 'all', label: 'All months' }, ...months];
  }, [rows]);

  const filteredRows = useMemo(
    () =>
      filterInventoryCapitalPlanRows(rows, {
        search: localSearch,
        categoryFilter,
        monthFilter,
        exceptionFilter,
      }),
    [rows, localSearch, categoryFilter, monthFilter, exceptionFilter]
  );

  const summary = useMemo(
    () => summarizeInventoryCapitalPlanRows(filteredRows),
    [filteredRows]
  );

  const monthSummaries = useMemo(
    () => buildInventoryCapitalMonthSummaries(filteredRows).slice(0, 8),
    [filteredRows]
  );

  const categorySummaries = useMemo(
    () => buildInventoryCapitalCategorySummaries(filteredRows).slice(0, 8),
    [filteredRows]
  );

  const forecastYear =
    inventoryCapitalPlanTableData?.forecastYear ??
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear;

  useEffect(() => {
    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;

    if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

    dispatch(
      getInventoryCapitalPlanTableData({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
      })
    );
  }, [
    dispatch,
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecast_year,
    selectedForecastDemandPlanning?.forecastYear,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
  ]);

  if (inventoryCapitalPlanTableLoading) {
    return (
      <section className="flex-1 px-4 py-4 lg:px-6">
        <div className="flex justify-center items-center h-full mt-8">
          <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
        </div>
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
            Inventory Capital Plan
          </span>
          <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
            Translate governed purchase units into category and month cash requirements
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            This finance view multiplies adjusted rounded units by BOM unit cost. It is not a PO
            execution workspace and does not read theoretical OTB quantities.
            {forecastYear ? ` Based on governed buys for ${forecastYear}.` : ''}
          </p>
        </div>
        <div className="flex flex-col gap-2 xl:flex-row xl:flex-wrap xl:items-center xl:justify-end">
          <input
            className="h-9 w-full min-w-[220px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 xl:w-[260px]"
            onChange={event => setLocalSearch(event.target.value)}
            placeholder="Search SKU, product, category, or supplier"
            value={localSearch}
          />
          <SelectDropdown
            options={categoryOptions}
            value={categoryFilter}
            onChange={value => setCategoryFilter(value)}
            placeholder="All categories"
            portal={true}
            {...selectDropdownClassnames}
          />
          <SelectDropdown
            options={monthOptions}
            value={monthFilter}
            onChange={value => setMonthFilter(value)}
            placeholder="All months"
            portal={true}
            {...selectDropdownClassnames}
          />
          <SelectDropdown
            options={EXCEPTION_FILTER_OPTIONS}
            value={exceptionFilter}
            onChange={value => setExceptionFilter(value)}
            placeholder="All finance rows"
            portal={true}
            {...selectDropdownClassnames}
          />
        </div>
      </div>

      <InventoryCapitalPlanSummaryBar summary={summary} />

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(280px,0.82fr)_minmax(0,1.75fr)]">
        <aside className="space-y-4">
          <InventoryCapitalPlanMonthRail monthSummaries={monthSummaries} />
          <InventoryCapitalPlanCategoryRail categorySummaries={categorySummaries} />
        </aside>
        <InventoryCapitalPlanMainTable rows={filteredRows} />
      </div>
    </section>
  );
};

export default InventoryCapitalPlanTable;
